import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { collection, query, where, getDocs, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { LINE_STATUSES, ORDER_STATUSES } from '../firebase/schema';
import { calculateOptimalPrintlist } from '../utils/printListCalculations';
import { startOfDay, endOfDay, differenceInCalendarDays, isWeekend, eachDayOfInterval } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/formatters';
import { getMonthlySalesProgress } from '../utils/monthlySalesProgress';
import { useFacility } from '../context/FacilityContext';
import { filterByFacility, facilityProgressDocId } from '../utils/facilities';

const WelcomeContainer = styled.div`
  padding: 40px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Montserrat', Arial, sans-serif;
  color: #FFFFFF;
  margin-top: 60px; // Account for navbar
`;

const Greeting = styled.h1`
  font-size: 2.5em;
  margin-bottom: 40px;
  color: var(--al-primary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
`;

const StatCard = styled.div`
  background: var(--al-surface);
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    transform: translateY(-4px);
    border-color: var(--al-primary);
  }
`;

const StatTitle = styled.h3`
  font-size: 1.1em;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatValue = styled.div`
  font-size: 2em;
  font-weight: bold;
  color: var(--al-primary);
`;

const FunFact = styled.div`
  background: var(--al-surface);
  padding: 24px;
  border-radius: 12px;
  margin-top: 40px;
  border-left: 4px solid var(--al-primary);
`;

const FunFactTitle = styled.h3`
  color: var(--al-primary);
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FunFactContent = styled.p`
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: var(--al-primary);
  font-size: 1.2em;
`;

const InspoLink = styled.a`
  display: inline-block;
  margin-top: 12px;
  color: var(--al-primary);
  text-decoration: none;
  font-size: 0.95em;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: color 0.2s ease, opacity 0.2s ease;

  &:hover {
    color: #7FE87F;
    opacity: 0.9;
  }
`;

const HotlistSection = styled.div`
  background: var(--al-surface);
  padding: 24px;
  border-radius: 12px;
  margin-top: 40px;
  border-left: 4px solid var(--al-primary);
`;

const HotlistTitle = styled.h3`
  color: var(--al-primary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 1.1em;
`;

const HotlistSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85em;
  margin-bottom: 16px;
`;

const HotlistTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Guard: never pass undefined to styled() (can cause "invalid element type" from App)
const hotlistRowStyles = `
  display: grid;
  grid-template-columns: 120px 1fr 100px;
  gap: 16px;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  color: inherit;
  text-decoration: none;
  transition: background 0.2s ease, border-color 0.2s ease;
  border: 1px solid transparent;

  &:hover {
    background: rgba(var(--al-primary-rgb), 0.08);
    border-color: rgba(var(--al-primary-rgb), 0.3);
  }
`;
const HotlistRowStyledLink = Link ? styled(Link)`${hotlistRowStyles}` : null;
const HotlistRowStyledDiv = styled.div`${hotlistRowStyles}`;
function HotlistRow({ to, children, ...rest }) {
  const navigate = useNavigate();
  if (HotlistRowStyledLink && to != null) {
    return <HotlistRowStyledLink to={to} {...rest}>{children}</HotlistRowStyledLink>;
  }
  const path = to && (typeof to === 'object' ? to.pathname : to);
  return (
    <HotlistRowStyledDiv
      role="link"
      tabIndex={0}
      onClick={() => path && navigate(path)}
      onKeyDown={(e) => e.key === 'Enter' && path && navigate(path)}
      {...rest}
    >
      {children}
    </HotlistRowStyledDiv>
  );
}

const HotlistPo = styled.span`
  font-weight: 600;
  color: var(--al-primary);
  font-size: 0.9em;
`;

const HotlistCustomer = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9em;
`;

const HotlistDue = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85em;
  text-align: right;
`;

const HotlistEmpty = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  padding: 12px 0;
`;

const SalesProgressSection = styled.div`
  background: var(--al-surface);
  padding: 24px;
  border-radius: 12px;
  margin-top: 40px;
  border-left: 4px solid var(--al-primary);
`;

const SalesProgressTitle = styled.h3`
  color: var(--al-primary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 1.1em;
`;

const SalesProgressSub = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 14px 0;
`;

const ProgressTrack = styled.div`
  position: relative;
  width: 100%;
  height: 20px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
`;

const PROGRESS_GREEN = 'var(--al-primary)';

const ProgressInvoiced = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${(props) => props.width}%;
  background: ${PROGRESS_GREEN};
`;

const ProgressWip = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${(props) => props.left}%;
  width: ${(props) => props.width}%;
  /* Same hue as headers / stat green, 20% opacity */
  background: rgba(var(--al-primary-rgb), 0.2);
`;

const ProgressDayMarker = styled.div`
  position: absolute;
  top: -2px;
  bottom: -2px;
  left: ${(props) => props.left}%;
  width: 2px;
  transform: translateX(-1px);
  background: #FF5F56;
`;

const SalesProgressLegend = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.86em;
`;

const FUN_FACTS = [
  "Screen printing has been around for over 1,000 years—the earliest known examples come from China during the Song Dynasty.",
  "Batch similar tasks together—your brain works more efficiently when it doesn't have to keep switching context.",
  "Cotton can hold up to 27 times its weight in water, which is why it's a favorite for dye and ink absorption in apparel.",
  "Writing down your top three priorities each morning increases the chance you'll actually get them done.",
  "The 2-minute rule: if something takes less than 2 minutes, do it now instead of adding it to a list.",
];

const PINTEREST_ADJECTIVES = [
  'cool', 'japan', 'designer', 'funky', 'vintage', 'minimal', 'bold',
  'retro', 'modern', 'creative', 'trendy', 'unique', 'edgy', 'clean'
];

const PINTEREST_CATEGORIES = [
  'shirt designs', 't-shirt ideas', 'typography', 'fonts', 'illustrations',
  'color combos', 'screen print', 'graphic design', 'apparel', 'logos'
];

// Interleave with Pinterest first so index 0 = Pinterest (shows on first visit / when storage is 0)
const DID_YOU_KNOW_CYCLE = FUN_FACTS.flatMap((fact) => ['pinterest', fact]);

const CYCLE_STORAGE_KEY = 'welcomeDidYouKnowCycleIndex';

const getAndAdvanceCycleIndex = () => {
  const current = parseInt(localStorage.getItem(CYCLE_STORAGE_KEY) || '0', 10);
  const next = (current + 1) % DID_YOU_KNOW_CYCLE.length;
  localStorage.setItem(CYCLE_STORAGE_KEY, String(next));
  return current;
};

const Welcome = () => {
  const { facilityId } = useFacility();
  const [todaysJobs, setTodaysJobs] = useState([]);
  const [ordersDueToday, setOrdersDueToday] = useState([]);
  const [loading, setLoading] = useState(true);
  const [didYouKnowCycleIndex, setDidYouKnowCycleIndex] = useState(null);
  const [salesProgress, setSalesProgress] = useState({
    goal: 0,
    mtdInvoiced: 0,
    notInvoicedTotal: 0
  });
  const [stats, setStats] = useState({
    totalJobs: 0,
    totalPieces: 0,
    screenPrintJobs: 0,
    dtgJobs: 0
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const monthlyProgress = await getMonthlySalesProgress(db);
        if (!cancelled) setSalesProgress(monthlyProgress);
      } catch (e) {
        console.error('Error loading monthly sales progress:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchTodaysJobs = async () => {
      try {
        const now = new Date();
        const isWeekday = !isWeekend(now);
        const isAfterLock = now.getHours() > 9 || (now.getHours() === 9 && now.getMinutes() >= 30);

        // First, try to fetch the locked print list for today
        if (isWeekday && isAfterLock) {
          const today = startOfDay(now);
          const lockedListRef = doc(
            db,
            'lockedPrintLists',
            facilityProgressDocId(facilityId, today.toISOString().split('T')[0])
          );
          const lockedListDoc = await getDoc(lockedListRef);
          
          if (lockedListDoc.exists()) {
            const lockedData = lockedListDoc.data();
            const screenPrintJobs = lockedData.screenPrint || [];
            const dtgJobs = lockedData.dtg || [];

            // Calculate stats from locked data
            const totalPieces = screenPrintJobs.reduce((sum, job) => sum + (job.totalQuantity || 0), 0) +
                              dtgJobs.reduce((sum, job) => sum + (job.totalQuantity || 0), 0);

            setStats({
              totalJobs: screenPrintJobs.length + dtgJobs.length,
              totalPieces,
              screenPrintJobs: screenPrintJobs.length,
              dtgJobs: dtgJobs.length
            });
            setDidYouKnowCycleIndex(getAndAdvanceCycleIndex());
            const todayStr = startOfDay(now).toISOString().split('T')[0];
            const todayEnd = endOfDay(now);
            const todayStart = startOfDay(now);
            const ordersSnapStr = await getDocs(query(collection(db, 'orders'), where('dueDate', '==', todayStr)));
            const ordersFromStr = ordersSnapStr.docs.map(d => ({ id: d.id, ...d.data() }));
            const qTs = query(
              collection(db, 'orders'),
              where('dueDate', '>=', Timestamp.fromDate(todayStart)),
              where('dueDate', '<=', Timestamp.fromDate(todayEnd))
            );
            let ordersDue = ordersFromStr;
            try {
              const ordersSnapTs = await getDocs(qTs);
              const merged = new Map(ordersFromStr.map(o => [o.id, o]));
              ordersSnapTs.docs.forEach(d => {
                if (!merged.has(d.id)) merged.set(d.id, { id: d.id, ...d.data() });
              });
              ordersDue = Array.from(merged.values());
            } catch (_) {
              // Range query may require index; use string-only results
            }
            const activeProduction = filterByFacility(ordersDue, facilityId).filter(
              (o) => o.status !== ORDER_STATUSES.READY_TO_INVOICE && o.status !== ORDER_STATUSES.COMPLETED && o.status !== ORDER_STATUSES.CANCELLED
            );
            setOrdersDueToday(activeProduction.sort((a, b) => String(a.poNumber ?? '').localeCompare(String(b.poNumber ?? ''))));
            setLoading(false);
            return;
          }
        }

        // If no locked list exists or it's before lock time, calculate from current data
        const q = query(
          collection(db, 'orderLines'),
          where('status', '==', LINE_STATUSES.READY_TO_PRINT)
        );
        const querySnapshot = await getDocs(q);
        const orderIds = [...new Set(querySnapshot.docs
          .map(doc => doc.data().orderId)
          .filter(id => id))];
        
        const orderDocs = await Promise.all(
          orderIds.map(async orderId => {
            const orderRef = doc(db, 'orders', orderId);
            return getDoc(orderRef);
          })
        );
        
        const orderMap = new Map(
          orderDocs
            .filter(doc => doc.exists())
            .map(doc => [doc.id, doc.data()])
        );

        const lines = filterByFacility(
          querySnapshot.docs.map(doc => {
            const lineData = doc.data();
            const orderData = orderMap.get(lineData.orderId) || {};
            return {
              id: doc.id,
              ...lineData,
              facilityId: lineData.facilityId || orderData.facilityId,
              dueDate: orderData.dueDate || lineData.dueDate,
              orderNumber: orderData.poNumber || 'N/A',
            };
          }),
          facilityId
        );

        // Filter for today's jobs
        const today = startOfDay(new Date());
        const todaysLines = lines.filter(line => {
          if (!line.dueDate) return false;
          const due = startOfDay(new Date(line.dueDate));
          return differenceInCalendarDays(due, today) <= 0;
        });

        setTodaysJobs(todaysLines);

        // Calculate stats using the same method as PrintList
        const result = calculateOptimalPrintlist(todaysLines);
        const screenPrintJobs = result.screenPrintJobs;
        const dtgJobs = result.dtgJobs;

        // Calculate total pieces
        const totalPieces = todaysLines.reduce((sum, line) => sum + (line.totalQuantity || 0), 0);

        setStats({
          totalJobs: todaysLines.length,
          totalPieces,
          screenPrintJobs: screenPrintJobs.length,
          dtgJobs: dtgJobs.length
        });
        setDidYouKnowCycleIndex(getAndAdvanceCycleIndex());

        const todayStr = today.toISOString().split('T')[0];
        const todayEnd = endOfDay(today);
        const ordersSnapStr = await getDocs(query(collection(db, 'orders'), where('dueDate', '==', todayStr)));
        const ordersFromStr = ordersSnapStr.docs.map(d => ({ id: d.id, ...d.data() }));
        const qTs = query(
          collection(db, 'orders'),
          where('dueDate', '>=', Timestamp.fromDate(today)),
          where('dueDate', '<=', Timestamp.fromDate(todayEnd))
        );
        let ordersDue = ordersFromStr;
        try {
          const ordersSnapTs = await getDocs(qTs);
          const merged = new Map(ordersFromStr.map(o => [o.id, o]));
          ordersSnapTs.docs.forEach(d => {
            if (!merged.has(d.id)) merged.set(d.id, { id: d.id, ...d.data() });
          });
          ordersDue = Array.from(merged.values());
        } catch (_) {
          // Range query may require index; use string-only results
        }
        const activeProduction = filterByFacility(ordersDue, facilityId).filter(
          (o) => o.status !== ORDER_STATUSES.READY_TO_INVOICE && o.status !== ORDER_STATUSES.COMPLETED && o.status !== ORDER_STATUSES.CANCELLED
        );
        setOrdersDueToday(activeProduction.sort((a, b) => String(a.poNumber ?? '').localeCompare(String(b.poNumber ?? ''))));
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTodaysJobs();
  }, [facilityId]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const handlePinterestInspo = (e) => {
    e.preventDefault();
    const adj = PINTEREST_ADJECTIVES[Math.floor(Math.random() * PINTEREST_ADJECTIVES.length)];
    const cat = PINTEREST_CATEGORIES[Math.floor(Math.random() * PINTEREST_CATEGORIES.length)];
    const searchQuery = `${adj} ${cat}`.trim();
    const url = `https://www.pinterest.com/search/pins/?q=${encodeURIComponent(searchQuery)}&rs=typed`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const cycleEntry = didYouKnowCycleIndex === null ? null : DID_YOU_KNOW_CYCLE[didYouKnowCycleIndex];
  const showPinterestInspo = cycleEntry === 'pinterest';
  const funFactContent = cycleEntry !== null && cycleEntry !== 'pinterest' ? cycleEntry : null;

  const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  const goal = Number(salesProgress.goal || 0);
  const mtdInvoiced = Number(salesProgress.mtdInvoiced || 0);
  const wip = Number(salesProgress.notInvoicedTotal || 0);
  const invoicedPct = goal > 0 ? Math.min((mtdInvoiced / goal) * 100, 100) : 0;
  const remainingPct = Math.max(0, 100 - invoicedPct);
  const wipPct = goal > 0 ? Math.min((wip / goal) * 100, remainingPct) : 0;
  const now = new Date();
  const monthStart = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
  const monthEnd = startOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  const todayStart = startOfDay(now);
  const countWeekdaysInclusive = (from, to) =>
    eachDayOfInterval({ start: from, end: to }).filter((d) => !isWeekend(d)).length;
  const weekdaysInMonth = countWeekdaysInclusive(monthStart, monthEnd);
  const weekdaysElapsed = countWeekdaysInclusive(monthStart, todayStart);
  const dayProgressPct =
    weekdaysInMonth > 0 ? Math.min((weekdaysElapsed / weekdaysInMonth) * 100, 100) : 0;

  if (loading) {
    return <LoadingContainer>Loading today's schedule...</LoadingContainer>;
  }

  return (
    <WelcomeContainer>
      <Greeting>{getGreeting()}, You Beautiful Soul!</Greeting>
      
      <StatsGrid>
        <StatCard>
          <StatTitle>Today's Jobs</StatTitle>
          <StatValue>{stats.totalJobs}</StatValue>
        </StatCard>
        
        <StatCard>
          <StatTitle>Total Pieces</StatTitle>
          <StatValue>{stats.totalPieces}</StatValue>
        </StatCard>
        
        <StatCard>
          <StatTitle>Screen Print Jobs</StatTitle>
          <StatValue>{stats.screenPrintJobs}</StatValue>
        </StatCard>
        
        <StatCard>
          <StatTitle>DTG Jobs</StatTitle>
          <StatValue>{stats.dtgJobs}</StatValue>
        </StatCard>
      </StatsGrid>

      <SalesProgressSection>
        <SalesProgressTitle>Monthly Sales Progress</SalesProgressTitle>
        <SalesProgressSub>
          Goal: {currency.format(goal)} | Invoiced: {currency.format(mtdInvoiced)} | Un-Invoiced WIP: {currency.format(wip)}
        </SalesProgressSub>
        <ProgressTrack>
          <ProgressInvoiced width={invoicedPct} />
          <ProgressWip left={invoicedPct} width={wipPct} />
          <ProgressDayMarker left={dayProgressPct} />
        </ProgressTrack>
        <SalesProgressLegend>
          <span>Solid green = invoiced month-to-date</span>
          <span>Faded green = open un-invoiced orders</span>
          <span>
            Red line = month elapsed on weekdays ({weekdaysElapsed} / {weekdaysInMonth} Mon–Fri)
          </span>
        </SalesProgressLegend>
      </SalesProgressSection>

      <HotlistSection>
        <HotlistTitle>Today&apos;s Hotlist</HotlistTitle>
        <HotlistSubtitle>Orders due today (all orders with this due date, not just the print list)</HotlistSubtitle>
        <HotlistTable>
          {ordersDueToday.length === 0 ? (
            <HotlistEmpty>No orders due today.</HotlistEmpty>
          ) : (
            ordersDueToday.map((order) => (
              <HotlistRow
                key={order.id}
                to={{ pathname: '/orders', state: { highlightOrderId: order.id } }}
              >
                <HotlistPo>{order.poNumber || order.id}</HotlistPo>
                <HotlistCustomer>{order.customerName || order.client || '—'}</HotlistCustomer>
                <HotlistDue>{order.dueDate ? formatDate(order.dueDate) : 'Today'}</HotlistDue>
              </HotlistRow>
            ))
          )}
        </HotlistTable>
      </HotlistSection>

      <FunFact>
        <FunFactTitle>Did You Know?</FunFactTitle>
        <FunFactContent>
          {showPinterestInspo ? (
            <InspoLink href="#" onClick={handlePinterestInspo}>
              Did you know…? Pinterest has cool ideas →
            </InspoLink>
          ) : (
            funFactContent ?? FUN_FACTS[0]
          )}
        </FunFactContent>
      </FunFact>
    </WelcomeContainer>
  );
};

export default Welcome; 